import React from 'react';
import { FaArrowLeft, FaExclamationTriangle, FaRedoAlt, FaTimes, FaTrashAlt } from 'react-icons/fa';
import './ForfyCommerceCockpit.css';

type ForfyCommerceRuntimeBoundaryProps = {
  children: React.ReactNode;
  onClose?: () => void;
  onReturnToSupport?: () => void;
};

type ForfyCommerceRuntimeBoundaryState = {
  hasError: boolean;
  message: string;
  incidentId: string;
};

const createIncidentId = () => `FORFY-${Date.now().toString(36).toUpperCase()}`;

class ForfyCommerceRuntimeBoundary extends React.Component<
  ForfyCommerceRuntimeBoundaryProps,
  ForfyCommerceRuntimeBoundaryState
> {
  state: ForfyCommerceRuntimeBoundaryState = {
    hasError: false,
    message: '',
    incidentId: createIncidentId(),
  };

  static getDerivedStateFromError(error: Error): ForfyCommerceRuntimeBoundaryState {
    return {
      hasError: true,
      message: error?.message || 'Erreur runtime Forfy Commerce',
      incidentId: createIncidentId(),
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('[Forfy Commerce runtime boundary]', {
      error,
      componentStack: errorInfo.componentStack,
    });
  }

  private retry = () => {
    this.setState({
      hasError: false,
      message: '',
      incidentId: createIncidentId(),
    });
  };

  private resetLocalForfyMemory = () => {
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        Object.keys(window.localStorage)
          .filter((key) => {
            const normalized = key.toLowerCase();
            return normalized.includes('forfy-commerce') || normalized.includes('forfycommerce');
          })
          .forEach((key) => window.localStorage.removeItem(key));
      }
    } catch (error) {
      console.warn('[Forfy Commerce runtime boundary] local reset skipped', error);
    }

    this.retry();
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    return (
      <section
        className="forfy-commerce-runtime-shield"
        role="alert"
        aria-label="Protection runtime Forfy Commerce"
      >
        <div className="forfy-commerce-runtime-shield__shell">
          <div className="forfy-commerce-runtime-shield__icon">
            <FaExclamationTriangle />
          </div>

          <div className="forfy-commerce-runtime-shield__content">
            <p className="forfy-commerce-runtime-shield__eyebrow">Forfy Commerce protégé</p>
            <h2>Forfy a intercepté une erreur avant l’écran noir.</h2>
            <p>
              L’assistant Commerce reste sécurisé. Aucune action financière, aucun paiement,
              aucun prix, aucun remboursement, aucune commission, aucun payout, aucune taxe et
              aucune donnée Giftshop ne sont modifiés.
            </p>

            <div className="forfy-commerce-runtime-shield__diagnostic">
              <span>Incident local</span>
              <strong>{this.state.incidentId}</strong>
              <small>{this.state.message}</small>
            </div>

            <div className="forfy-commerce-runtime-shield__actions">
              <button type="button" onClick={this.retry}>
                <FaRedoAlt /> Réessayer Forfy
              </button>

              <button type="button" onClick={this.resetLocalForfyMemory}>
                <FaTrashAlt /> Réinitialiser mémoire locale Forfy
              </button>

              {this.props.onReturnToSupport ? (
                <button type="button" onClick={this.props.onReturnToSupport}>
                  <FaArrowLeft /> Retour Support
                </button>
              ) : null}

              {this.props.onClose ? (
                <button type="button" onClick={this.props.onClose}>
                  <FaTimes /> Fermer
                </button>
              ) : null}
            </div>
          </div>
        </div>
      </section>
    );
  }
}

export default ForfyCommerceRuntimeBoundary;
